import { createFeatureSelector, createSelector } from '@ngrx/store';
import { concat, isNil, toNumber } from 'lodash';
import { QActiveAnswerQuestion } from './active.interface';
import {
  QuestionnaireActiveState,
  questionnaireActiveKey
} from './active.state';
import { qUtilsActiveIsDateGreaterThanOneMonth } from './active.utils';

export const selectQuestionnaireActive =
  createFeatureSelector<QuestionnaireActiveState>(questionnaireActiveKey);

export const selectQuestionnaireActiveUserProfile = createSelector(
  selectQuestionnaireActive,
  (state) => state.userProfile
);

export const selectQuestionnaireActiveLoading = createSelector(
  selectQuestionnaireActive,
  (state: QuestionnaireActiveState) => state.loading
);

export const selectQuestionnaireActiveForm = createSelector(
  selectQuestionnaireActive,
  (state) => state.form
);

export const selectQuestionnaireActiveFormValidation = createSelector(
  selectQuestionnaireActive,
  selectQuestionnaireActiveUserProfile,
  (state, uProfile) => {
    const form = state.form;
    const value: Array<boolean> = [];

    // weight
    if (
      uProfile &&
      qUtilsActiveIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.weight ?? ''
      )
    ) {
      if (form.information.weight === null || form.information.weight <= 0) {
        value.push(false);
      }
    }
    // height
    if (
      uProfile &&
      qUtilsActiveIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.height ?? ''
      )
    ) {
      if (form.information.height === null || form.information.height <= 0) {
        value.push(false);
      }
    }
    if (form.medicalHistory.length === 0) {
      value.push(false);
    }
    if (
      (Array.isArray(form.exerciseCurrentDetails.exerciseCurrently) &&
        form.exerciseCurrentDetails.exerciseCurrently.length === 0) ||
      isNil(form.exerciseCurrentDetails.exerciseCurrently)
    ) {
      value.push(false);
    }

    return value.every((v) => v);
  }
);

export const selectQuestionnaireActiveFormForSubmit = createSelector(
  selectQuestionnaireActive,
  (state) => {
    const form = state.form;
    const information = {
      id: null,
      height: form.information.height ?? 0,
      weight: form.information.weight ?? 0,
      heightUnit: 'cm',
      weighUnit: 'kg'
    };
    const [medicalHistory, medicalHistoryOthers] = [
      form.medicalHistory
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          medicalId: null,
          medicalCompletedName: [o.value, o.description].join(' ').trim(),
          medicalNameEn: o.value,
          medicalNameId: o.value,
          medicalType: 'DISEASE',
          medicalIsOther: false
        })),
      form.medicalHistory
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          medicalId: null,
          medicalCompletedName: v,
          medicalNameEn: v,
          medicalNameId: v,
          medicalType: 'DISEASE',
          medicalIsOther: true
        }))
        .filter(Boolean) ?? []
    ];

    const value: QActiveAnswerQuestion = {
      information,
      medicalHistory: concat(medicalHistory, medicalHistoryOthers),
      fitnessGoals: form.fitnessGoals?.value ?? '',
      injuryHistory: [form.injuryHistory?.value ?? ''].filter(Boolean),
      exerciseCurrentDetails: {
        exerciseCurrently: form.exerciseCurrentDetails.exerciseCurrently.map(
          (o) => (o.value === 'OTHER' ? o.name : o.value)
        ),
        exerciseMinutesPerSession:
          form.exerciseCurrentDetails.exerciseMinutesPerSession?.value ?? '',
        exerciseMinutesPerSessionValue:
          form.exerciseCurrentDetails.exerciseMinutesPerSession
            ?.metCalculation ?? 0,
        exerciseTimesPerWeek:
          form.exerciseCurrentDetails.exerciseTimesPerWeek?.value === 'OTHER'
            ? [
                form.exerciseCurrentDetails.exerciseTimesPerWeek.name,
                'time per week'
              ]
                .filter(Boolean)
                .join(' ')
            : (form.exerciseCurrentDetails.exerciseTimesPerWeek?.value ?? ''),
        exerciseTimesPerWeekValue:
          form.exerciseCurrentDetails.exerciseTimesPerWeek?.value === 'OTHER'
            ? toNumber(form.exerciseCurrentDetails.exerciseTimesPerWeek.name)
            : (form.exerciseCurrentDetails.exerciseTimesPerWeek
                ?.metCalculation ?? 0)
      }
    };
    return value;
  }
);

export const selectQuestionnaireActiveSubmissionResponse = createSelector(
  selectQuestionnaireActive,
  (state) => state.submissionResponse
);
